import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ESTADOS_BR = [
  { uf: "AC", nome: "Acre" }, { uf: "AL", nome: "Alagoas" }, { uf: "AP", nome: "Amapa" },
  { uf: "AM", nome: "Amazonas" }, { uf: "BA", nome: "Bahia" }, { uf: "CE", nome: "Ceara" },
  { uf: "DF", nome: "Distrito Federal" }, { uf: "ES", nome: "Espirito Santo" }, { uf: "GO", nome: "Goias" },
  { uf: "MA", nome: "Maranhao" }, { uf: "MT", nome: "Mato Grosso" }, { uf: "MS", nome: "Mato Grosso do Sul" },
  { uf: "MG", nome: "Minas Gerais" }, { uf: "PA", nome: "Para" }, { uf: "PB", nome: "Paraiba" },
  { uf: "PR", nome: "Parana" }, { uf: "PE", nome: "Pernambuco" }, { uf: "PI", nome: "Piaui" },
  { uf: "RJ", nome: "Rio de Janeiro" }, { uf: "RN", nome: "Rio Grande do Norte" },
  { uf: "RS", nome: "Rio Grande do Sul" }, { uf: "RO", nome: "Rondonia" }, { uf: "RR", nome: "Roraima" },
  { uf: "SC", nome: "Santa Catarina" }, { uf: "SP", nome: "Sao Paulo" }, { uf: "SE", nome: "Sergipe" },
  { uf: "TO", nome: "Tocantins" }
];

const emptyForm = {
  name: "", client_code: "", cnpj: "", email: "", phone: "",
  contact_person: "", cep: "", estado: "", cidade: "", complemento: "",
  address: "", delivery_address: "", notes: ""
};

function maskCep(value) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 5) return digits.slice(0, 5) + "-" + digits.slice(5);
  return digits;
}

export default function ClientsPage() {
  const { can } = useAuth();
  const canDelete = can("clientes", "delete");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [cepLoading, setCepLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch { toast.error("Erro ao carregar clientes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({
      name: client.name || "", client_code: client.client_code || "",
      cnpj: client.cnpj || "", email: client.email || "",
      phone: client.phone || "", contact_person: client.contact_person || "",
      cep: client.cep || "", estado: client.estado || "",
      cidade: client.cidade || "", complemento: client.complemento || "",
      address: client.address || "", delivery_address: client.delivery_address || "",
      notes: client.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/clients/${editing.id}`, form, { withCredentials: true });
        toast.success("Cliente atualizado");
      } else {
        await axios.post(`${API}/clients`, form, { withCredentials: true });
        toast.success("Cliente cadastrado");
      }
      setDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar cliente");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusao do cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
      toast.success("Cliente removido");
      fetchClients();
    } catch { toast.error("Erro ao remover cliente"); }
  };

  const f = (v) => setForm((prev) => ({ ...prev, ...v }));

  const handleCepChange = async (rawValue) => {
    const masked = maskCep(rawValue);
    f({ cep: masked });
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 8) {
      setCepLoading(true);
      try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${digits}/json/`);
        if (data.erro) {
          toast.error("CEP nao encontrado");
        } else {
          f({
            cidade: data.localidade || "",
            estado: data.uf || "",
            address: data.logradouro ? `${data.logradouro}${data.bairro ? ", " + data.bairro : ""}` : form.address,
            complemento: data.complemento || form.complemento
          });
          toast.success("Endereco preenchido automaticamente");
        }
      } catch {
        toast.error("Erro ao buscar CEP");
      } finally {
        setCepLoading(false);
      }
    }
  };

  const getEstadoNome = (uf) => {
    const e = ESTADOS_BR.find((s) => s.uf === uf);
    return e ? `${e.nome} (${e.uf})` : uf || "-";
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Clientes</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerencie seus clientes</p>
        </div>
        {can("clientes", "create") && (
        <Button onClick={openCreate} data-testid="create-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Cliente
        </Button>
        )}
      </div>

      <Card className="border border-border shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codigo</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>CNPJ</TableHead>
                <TableHead>Cidade/UF</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead className="w-[100px]">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhum cliente cadastrado</TableCell></TableRow>
              ) : (
                clients.map((c) => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.client_code || "-"}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm">{c.cnpj || "-"}</TableCell>
                    <TableCell className="text-sm">{c.cidade && c.estado ? `${c.cidade}/${c.estado}` : c.cidade || c.estado || "-"}</TableCell>
                    <TableCell className="text-sm">{c.contact_person || "-"}</TableCell>
                    <TableCell className="text-sm">{c.phone || "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelected(c); setDetailOpen(true); }} data-testid={`view-client-${c.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {can("clientes", "edit") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.id)} data-testid={`delete-client-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
            <DialogDescription>Preencha os dados do cliente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codigo do Cliente</Label>
                <Input value={form.client_code} onChange={(e) => f({ client_code: e.target.value })} placeholder="Ex: CLI-001" data-testid="client-code-input" />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input value={form.cnpj} onChange={(e) => f({ cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="client-cnpj-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nome / Razao Social *</Label>
              <Input value={form.name} onChange={(e) => f({ name: e.target.value })} required data-testid="client-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Pessoa de Contato</Label>
              <Input value={form.contact_person} onChange={(e) => f({ contact_person: e.target.value })} placeholder="Nome do contato na empresa" data-testid="client-contact-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => f({ email: e.target.value })} data-testid="client-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={(e) => f({ phone: e.target.value })} data-testid="client-phone-input" />
              </div>
            </div>

            {/* CEP + Cidade + Estado */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>CEP</Label>
                <div className="relative">
                  <Input
                    value={form.cep}
                    onChange={(e) => handleCepChange(e.target.value)}
                    placeholder="00000-000"
                    maxLength={9}
                    data-testid="client-cep-input"
                  />
                  {cepLoading && (
                    <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => f({ estado: v })}>
                  <SelectTrigger data-testid="client-estado-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BR.map((e) => (
                      <SelectItem key={e.uf} value={e.uf}>{e.uf} - {e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input value={form.cidade} onChange={(e) => f({ cidade: e.target.value })} placeholder="Cidade" data-testid="client-cidade-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Endereco</Label>
              <Input value={form.address} onChange={(e) => f({ address: e.target.value })} placeholder="Rua, numero, bairro" data-testid="client-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Complemento</Label>
              <Input value={form.complemento} onChange={(e) => f({ complemento: e.target.value })} placeholder="Sala, andar, bloco..." data-testid="client-complemento-input" />
            </div>
            <div className="space-y-2">
              <Label>Local de Entrega</Label>
              <Input value={form.delivery_address} onChange={(e) => f({ delivery_address: e.target.value })} placeholder="Endereco de entrega (se diferente)" data-testid="client-delivery-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Observacoes</Label>
              <Textarea value={form.notes} onChange={(e) => f({ notes: e.target.value })} rows={2} data-testid="client-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="client-submit-btn">{editing ? "Salvar" : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
                <DialogDescription>{selected.client_code ? `Codigo: ${selected.client_code}` : "Cliente"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">CNPJ</span>
                    <p className="font-medium mt-0.5">{selected.cnpj || "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Telefone</span>
                    <p className="font-medium mt-0.5">{selected.phone || "-"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Email</span>
                    <p className="font-medium mt-0.5">{selected.email || "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Pessoa de Contato</span>
                    <p className="font-medium mt-0.5">{selected.contact_person || "-"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">CEP</span>
                    <p className="font-medium mt-0.5">{selected.cep || "-"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Estado</span>
                    <p className="font-medium mt-0.5">{getEstadoNome(selected.estado)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Cidade</span>
                    <p className="font-medium mt-0.5">{selected.cidade || "-"}</p>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground uppercase">Endereco</span>
                  <p className="font-medium mt-0.5">{selected.address || "-"}</p>
                </div>
                {selected.complemento && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Complemento</span>
                    <p className="font-medium mt-0.5">{selected.complemento}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-muted-foreground uppercase">Local de Entrega</span>
                  <p className="font-medium mt-0.5">{selected.delivery_address || "-"}</p>
                </div>
                {selected.notes && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Observacoes</span>
                    <p className="font-medium mt-0.5">{selected.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
